import React, { useRef, useState } from 'react';
import { Bold, Italic, Link as LinkIcon, Heading, List as ListIcon } from 'lucide-react';

/* Lightweight markdown-input · a textarea with a small toolbar that
 * inserts / wraps the markdown syntax already supported by the
 * Newsletters renderer (renderBody / renderInline in pages/Newsletters.jsx):
 *
 *   **bold**   ·  *italic*   ·  [text](url)   ·  ### heading   ·  - bullet
 *
 * The component is fully controlled · `value` / `onChange(nextString)`.
 * Selection is preserved after each insert so editors can chain
 * actions (select word → click B → keep typing).
 */
export default function MarkdownTextarea({
  value,
  onChange,
  rows = 6,
  placeholder,
  className = '',
  disabled = false,
}) {
  const ref = useRef(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [pendingSelection, setPendingSelection] = useState(null);
  const [linkText, setLinkText] = useState('');
  const [linkUrl, setLinkUrl] = useState('');

  /* Replace the current selection with `before + selected + after`,
   * then restore the caret inside the new content so the user can
   * keep typing without re-selecting. */
  const wrap = (before, after = before) => {
    const ta = ref.current;
    if (!ta) return;
    const start = ta.selectionStart ?? value.length;
    const end = ta.selectionEnd ?? value.length;
    const selected = value.slice(start, end);
    const next = value.slice(0, start) + before + selected + after + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      // Nothing selected: the caret lands between the markers so the user
      // types straight into the bold / italic run · no placeholder word
      // that then has to be deleted (one draft had `**gras**` saved as its
      // whole teaser).
      ta.setSelectionRange(start + before.length, start + before.length + selected.length);
    });
  };

  const insertPrefix = (prefix) => {
    const ta = ref.current;
    if (!ta) return;
    const start = ta.selectionStart ?? 0;
    // Find the start of the current line.
    const lineStart = value.lastIndexOf('\n', start - 1) + 1;
    const next = value.slice(0, lineStart) + prefix + value.slice(lineStart);
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(start + prefix.length, start + prefix.length);
    });
  };

  /* Two-step link picker · grab the current selection before opening
   * the dialog so it survives the input getting focus stolen. */
  const openLinkPicker = () => {
    const ta = ref.current;
    if (!ta) return;
    const start = ta.selectionStart ?? value.length;
    const end = ta.selectionEnd ?? value.length;
    setPendingSelection({ start, end });
    setLinkText(value.slice(start, end));
    setLinkUrl('');
    setLinkOpen(true);
  };
  const cancelLink = () => { setLinkOpen(false); setPendingSelection(null); };
  const confirmLink = () => {
    if (!pendingSelection || !linkUrl.trim()) { cancelLink(); return; }
    const { start, end } = pendingSelection;
    const label = (linkText.trim() || 'lien');
    const md = `[${label}](${linkUrl.trim()})`;
    const next = value.slice(0, start) + md + value.slice(end);
    onChange(next);
    setLinkOpen(false);
    setPendingSelection(null);
    requestAnimationFrame(() => {
      const ta = ref.current;
      if (!ta) return;
      ta.focus();
      // Place the caret just after the inserted link.
      const pos = start + md.length;
      ta.setSelectionRange(pos, pos);
    });
  };

  const onKeyDown = (e) => {
    if (!(e.metaKey || e.ctrlKey)) return;
    const k = e.key.toLowerCase();
    if (k === 'b') { e.preventDefault(); wrap('**', '**'); }
    else if (k === 'i') { e.preventDefault(); wrap('*', '*'); }
    else if (k === 'k') { e.preventDefault(); openLinkPicker(); }
  };

  return (
    <div className="relative w-full block">
      <div className="flex items-center gap-1 mb-1.5">
        <ToolBtn onClick={() => wrap('**', '**')} title="Gras (⌘B)" disabled={disabled}>
          <Bold className="h-3.5 w-3.5" />
        </ToolBtn>
        <ToolBtn onClick={() => wrap('*', '*')} title="Italique (⌘I)" disabled={disabled}>
          <Italic className="h-3.5 w-3.5" />
        </ToolBtn>
        <ToolBtn onClick={openLinkPicker} title="Lien (⌘K)" disabled={disabled}>
          <LinkIcon className="h-3.5 w-3.5" />
        </ToolBtn>
        <span className="mx-1 h-4 w-px bg-[hsl(var(--rule))]" aria-hidden />
        <ToolBtn onClick={() => insertPrefix('### ')} title="Titre" disabled={disabled}>
          <Heading className="h-3.5 w-3.5" />
        </ToolBtn>
        <ToolBtn onClick={() => insertPrefix('- ')} title="Liste à puces" disabled={disabled}>
          <ListIcon className="h-3.5 w-3.5" />
        </ToolBtn>
      </div>
      <textarea
        ref={ref}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        rows={rows}
        placeholder={placeholder}
        disabled={disabled}
        className={`ed-textarea ${className}`}
      />
      {linkOpen && (
        <LinkPicker
          text={linkText} url={linkUrl}
          onText={setLinkText} onUrl={setLinkUrl}
          onCancel={cancelLink} onConfirm={confirmLink}
        />
      )}
    </div>
  );
}

const ToolBtn = ({ children, onClick, title, disabled }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}  // keep textarea focus
    onClick={onClick}
    disabled={disabled}
    title={title}
    aria-label={title}
    className="inline-flex items-center justify-center h-7 w-7 rounded-md text-[hsl(var(--text-soft))] hover:text-[hsl(var(--text))] hover:bg-[hsl(var(--surface-2))] disabled:opacity-40 disabled:hover:bg-transparent transition"
  >
    {children}
  </button>
);

const LinkPicker = ({ text, url, onText, onUrl, onCancel, onConfirm }) => (
  <div className="absolute inset-x-0 top-9 z-20 rounded-md border border-border bg-[hsl(var(--surface))] shadow-lg p-3 space-y-2"
       onMouseDown={(e) => e.stopPropagation()}>
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-[hsl(var(--text-muted))] mb-1">
        Texte affiché
      </label>
      <input
        type="text"
        value={text}
        onChange={(e) => onText(e.target.value)}
        placeholder="Cliquez ici"
        className="ed-input w-full"
        autoFocus
      />
    </div>
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-[hsl(var(--text-muted))] mb-1">
        URL
      </label>
      <input
        type="url"
        value={url}
        onChange={(e) => onUrl(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') onConfirm(); else if (e.key === 'Escape') onCancel(); }}
        placeholder="https://"
        className="ed-input w-full font-num text-[13px]"
      />
    </div>
    <div className="flex justify-end gap-2 pt-1">
      <button type="button" onClick={onCancel}
              className="text-[12px] px-2 py-1 rounded text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]">
        Annuler
      </button>
      <button type="button" onClick={onConfirm}
              className="text-[12px] px-3 py-1 rounded bg-[hsl(var(--text))] text-[hsl(var(--bg))] hover:opacity-90">
        Insérer
      </button>
    </div>
  </div>
);
